import {
    useSuspenseQuery,
    UseSuspenseQueryResult,
    UseMutationResult,
    useMutation,
    useQueryClient,
    useQuery,
    UseQueryResult,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import { saveFile } from '@Helpers/saveFile';
import {
    paginatedBaselineResponseSchema,
    baselineSchema,
    baselineFiltersResponseSchema,
    baselineListResponseSchema,
    pushdownLocationsResponseSchema,
    previewPushdownResponseSchema,
    BaselinesSearch,
    PaginatedBaselineResponse,
    Baseline,
    BaselineFiltersRequest,
    BaselineFiltersResponse,
    BaselineListResponse,
    PushdownLocationsResponse,
    PushdownRequest,
    PreviewPushdownResponse,
    CopyBaselineRequest,
    ImportBaselineRequest,
    CopyBaselineResponse,
} from './baseline.types';
import { mutationResponseSchema, MutationResponse } from '../shared.types';

const SEARCH_QUERY_KEY = 'BaselinesSearch';
const GET_QUERY_KEY = 'BaselineGet';
const FILTERS_QUERY_KEY = 'BaselineFilters';
const LIST_QUERY_KEY = 'BaselineList';
const PUSHDOWN_LOCATIONS_QUERY_KEY = 'BaselinePushdownLocations';
const PREVIEW_PUSHDOWN_QUERY_KEY = 'BaselinePreviewPushdown';

// ---- Search ----

const searchBaselines = async (searchRequest: BaselinesSearch): Promise<PaginatedBaselineResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}baseline/search`, { json: searchRequest }).json();
    return paginatedBaselineResponseSchema.parse(response);
};

export const useSearchBaselines = (searchRequest: BaselinesSearch): UseSuspenseQueryResult<PaginatedBaselineResponse> => {
    return useSuspenseQuery<PaginatedBaselineResponse>({
        queryKey: [SEARCH_QUERY_KEY, searchRequest],
        queryFn: () => searchBaselines(searchRequest),
    });
};

// ---- Get Single ----

const getBaseline = async (id: number): Promise<Baseline> => {
    const response = await createPacerApi().get(`${API_ROOT}baseline/${id}`).json();
    return baselineSchema.parse(response);
};


export const useGetBaseline = (id: number, options?: { enabled?: boolean }): UseQueryResult<Baseline> => {
    return useQuery<Baseline>({
        queryKey: [GET_QUERY_KEY, id],
        queryFn: () => getBaseline(id),
        enabled: options?.enabled !== undefined ? options.enabled : true,
    });
};

// ---- Load Filters ----

const loadFilters = async (filterRequest: BaselineFiltersRequest): Promise<BaselineFiltersResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}baseline/filters`, { json: filterRequest }).json();
    return baselineFiltersResponseSchema.parse(response);
};


export const useLoadBaselineFilters = (filterRequest: BaselineFiltersRequest, options?: { enabled?: boolean }): UseQueryResult<BaselineFiltersResponse> => {
    return useQuery<BaselineFiltersResponse>({
        queryKey: [FILTERS_QUERY_KEY, filterRequest],
        queryFn: () => loadFilters(filterRequest),
        enabled: options?.enabled !== undefined ? options.enabled : true,
    });
};

// ---- Load List ----

const loadList = async (subscriptionId: number): Promise<BaselineListResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}baseline/list`, { json: { subscriptionId } }).json();
    return baselineListResponseSchema.parse(response);
};

export const useLoadBaselineList = (subscriptionId: number): UseSuspenseQueryResult<BaselineListResponse> => {
    return useSuspenseQuery<BaselineListResponse>({
        queryKey: [LIST_QUERY_KEY, subscriptionId],
        queryFn: () => loadList(subscriptionId),
    });
};

// ---- Save (Create / Update) ----

const saveBaseline = async (baseline: Baseline): Promise<MutationResponse> => {
    let response;
    if (baseline.id && baseline.id > 0) {
        // Existing baseline - update
        response = await createPacerApi().put(`${API_ROOT}baseline/update`, { json: baseline }).json();
    } else {
        // New baseline - create
        response = await createPacerApi().post(`${API_ROOT}baseline/create`, { json: baseline }).json();
    }
    return mutationResponseSchema.parse(response);
};

export const useSaveBaseline = (callback?: (results: MutationResponse) => void): UseMutationResult<MutationResponse, Error, Baseline> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, Baseline>({
        mutationFn: saveBaseline,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);
            queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
        },
    });
};

// ---- Load Pushdown Locations ----

const loadPushdownLocations = async (): Promise<PushdownLocationsResponse> => {
    const response = await createPacerApi().get(`${API_ROOT}baseline/pushdown-locations`).json();
    return pushdownLocationsResponseSchema.parse(response);
};

export const useLoadPushdownLocations = (): UseSuspenseQueryResult<PushdownLocationsResponse> => {
    return useSuspenseQuery<PushdownLocationsResponse>({
        queryKey: [PUSHDOWN_LOCATIONS_QUERY_KEY],
        queryFn: loadPushdownLocations,
    });
};

// ---- Pushdown ----

const pushdownBaseline = async (request: PushdownRequest): Promise<MutationResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}baseline/pushdown`, { json: request }).json();
    return mutationResponseSchema.parse(response);
};

export const usePushdownBaseline = (callback?: (results: MutationResponse) => void): UseMutationResult<MutationResponse, Error, PushdownRequest> => {
    return useMutation<MutationResponse, Error, PushdownRequest>({
        mutationFn: pushdownBaseline,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);
        },
    });
};

// ---- Preview Pushdown ----

const previewPushdown = async (baselineId: number, openingDate: string): Promise<PreviewPushdownResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}baseline/preview-pushdown`, { json: { baselineId, openingDate } }).json();
    return previewPushdownResponseSchema.parse(response);
};

export const usePreviewPushdown = (baselineId: number, openingDate: string): UseQueryResult<PreviewPushdownResponse> => {
    return useQuery<PreviewPushdownResponse>({
        queryKey: [PREVIEW_PUSHDOWN_QUERY_KEY, baselineId, openingDate],
        queryFn: () => previewPushdown(baselineId, openingDate),
        enabled: !!baselineId && !!openingDate,
    });
};

// ---- Import ----

const importBaseline = async (request: ImportBaselineRequest): Promise<MutationResponse> => {
    const body = new FormData();
    body.append('file', request.file);
    body.append('baselineId', String(request.baselineId));

    const response = await createPacerApi().post(`${API_ROOT}baseline/import`, { body }).json();
    return mutationResponseSchema.parse(response);
};

export const useImportBaseline = (
    callback?: (results: MutationResponse) => void
): UseMutationResult<MutationResponse, Error, ImportBaselineRequest> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, ImportBaselineRequest>({
        mutationFn: importBaseline,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);

            queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
        },
    });
};

// ---- Import Template (file download) ----

const downloadImportTemplate = async (templateType: string = ''): Promise<void> => {
    const response = await createPacerApi().get(`${API_ROOT}baseline/import-template/${templateType}`);

    await saveFile(response);
};

export const useDownloadImportTemplate = (callback?: () => void): UseMutationResult<void, Error, string> => {
    return useMutation<void, Error, string>({
        mutationFn: (templateType) => downloadImportTemplate(templateType),
        onSuccess: () => {
            callback?.();
        },
    });
};

const copyBaseline = async (request: CopyBaselineRequest): Promise<CopyBaselineResponse> => {
    const response = await createPacerApi().post(`${API_ROOT}baseline/copy`, { json: request }).json();
    return response as CopyBaselineResponse;
};

export const useCopyBaseline = (
    callback?: (results: CopyBaselineResponse) => void
): UseMutationResult<CopyBaselineResponse, Error, CopyBaselineRequest> => {
    const queryClient = useQueryClient();
    return useMutation<CopyBaselineResponse, Error, CopyBaselineRequest>({
        mutationFn: copyBaseline,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);

            queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
        },
    });
};
